import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, LogOut } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import AdminDashboard from "../components/admin/AdminDashboard";
import ChangePasswordDialog from "../components/admin/ChangePasswordDialog";
import ForgotPasswordForm from "../components/admin/ForgotPasswordForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail.msg || String(detail);
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const onUnauthorized = useCallback(() => setUser(false), []);

  useEffect(() => {
    document.title = "Admin | Andy Watson IT";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
    return () => meta.remove();
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-home-link">
            <span className="h-8 w-8 rounded-lg bg-sky-500 text-white text-xs font-bold flex items-center justify-center">AW</span>
            <span className="font-semibold">Admin</span>
          </Link>
          {user && (
            <div className="flex gap-2">
              <ChangePasswordDialog />
              <Button variant="outline" onClick={logout} data-testid="admin-logout-btn" className="rounded-full h-9">
                <LogOut className="h-4 w-4 mr-1.5" /> Log out
              </Button>
            </div>
          )}
        </div>
      </header>
      {user === null && <div className="p-20 text-center text-slate-500" data-testid="admin-loading">Loading…</div>}
      {user === false && <AdminLogin onLogin={setUser} />}
      {user && <AdminDashboard onUnauthorized={onUnauthorized} />}
    </div>
  );
}

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (forgot)
    return (
      <div className="max-w-sm mx-auto px-6 py-20">
        <div className="rounded-3xl border border-slate-200 bg-white p-8">
          <ForgotPasswordForm onBack={() => setForgot(false)} />
        </div>
      </div>
    );

  return (
    <div className="max-w-sm mx-auto px-6 py-20">
      <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-8 space-y-5" data-testid="admin-login-form">
        <div className="h-11 w-11 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center">
          <Lock className="h-5 w-5" />
        </div>
        <h1 className="text-2xl font-semibold">Admin login</h1>
        <div>
          <Label htmlFor="admin-email">Email</Label>
          <Input id="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input" className="mt-2 h-11" autoComplete="username" />
        </div>
        <div>
          <Label htmlFor="admin-password">Password</Label>
          <Input id="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input" className="mt-2 h-11" autoComplete="current-password" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="admin-login-submit"
          className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white">
          {loading ? "Signing in…" : "Sign in"}
        </Button>
        <button type="button" onClick={() => setForgot(true)} data-testid="admin-forgot-link"
          className="block mx-auto text-sm text-sky-700 hover:underline">
          Forgot password?
        </button>
      </form>
    </div>
  );
};
